import LicenseService from 'business/services/LicenseService';
import { FmlxButton, FmlxCard } from 'fmlx-common-ui';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LicenseHeader from './LicenseHeader';
import LicenseActivation from './modals/LicenseActivation';
import LicenseRequest from './modals/LicenseRequest';
import { RMLicenseHttpStatusCode } from './RMLicenseHttpStatusCode';
import LicenseNotActivated from './violation/LicenseNotActivated';
import LicenseExpired from './violation/LicenseExpired';
import { AUTH_FLOW_PATHS } from '../constants';

// to prevent unsave redirect path and make user back license-error loops
const getSafeNotificationReturnTo = (returnTo?: string): string | null => {
  if (
    !returnTo?.startsWith('/') ||
    returnTo.startsWith('//') ||
    AUTH_FLOW_PATHS.some(
      (authPath) => authPath !== '/featureselection' && returnTo.startsWith(authPath),
    )
  ) {
    return null;
  }

  return returnTo;
};

function LicenseViolationBody({
  headerText = 'License',
  children,
  style = null,
  statusCode = null,
}) {
  return (
    <div id="license-violation-body-style" style={{ width: '100%', ...style }}>
      <div
        id="license-violation-body-header"
        className="license-header"
        style={{ alignSelf: 'start', marginLeft: 24 }}
      >
        {statusCode == RMLicenseHttpStatusCode.LicenseUpdating && 'License Update'}
        {statusCode == RMLicenseHttpStatusCode.ExpiryViolation && 'Expired License'}
        {statusCode == null && headerText}
      </div>
      <div
        id="license-violation-body-license-text"
        style={{
          borderTop: '1px solid #E0E0E0 ',
          borderBottom: '1px solid #E0E0E0',
          width: '100%',
          padding: '16px 12px 48px 24px',
          marginTop: 15,
        }}
        className="license-text"
      >
        {children}
      </div>
    </div>
  );
}

export default function LicenseViolation() {
  const location = useLocation();
  const navigate = useNavigate();
  const httpStatus = location.state?.detail ?? 0;
  const httpMessage = location.state?.message;
  const [showLicenseRequestModal, setShowLicenseRequestModal] = useState(false);
  const [showLicenseActivationModal, setShowLisenceActivationModal] = useState(false);

  async function closeWindow() {
    if (httpStatus === RMLicenseHttpStatusCode.LicenseNotification) {
      const returnTo = getSafeNotificationReturnTo(location.state?.returnTo);
      navigate(returnTo ?? '/', { replace: true });
    } else if (httpStatus === RMLicenseHttpStatusCode.LicenseTerminated) {
      const licService = new LicenseService();
      await licService.endLicenseSession();
      navigate('/logout');
    } else if (
      httpStatus === RMLicenseHttpStatusCode.ExpiryViolation ||
      httpStatus === RMLicenseHttpStatusCode.FeatureViolation
    ) {
      {
        navigate('/logout');
      }
    } else if (httpStatus === RMLicenseHttpStatusCode.EditionViolation) {
      window.location.replace('/');
    } else {
      navigate('/');
    }
  }

  function activateButtonClicked() {
    setShowLisenceActivationModal(true);
  }

  function requestButtonClicked() {
    setShowLicenseRequestModal(true);
  }

  function adminToolsButtonClicked() {
    navigate('/settings/license', { state: { detail: 'License' } });
  }

  if (httpStatus === RMLicenseHttpStatusCode.LicenseNotActive) {
    return <LicenseNotActivated />;
  }
  if (httpStatus === RMLicenseHttpStatusCode.ExpiryViolation) {
    return <LicenseExpired />;
  }

  return (
    <div id="license-violation-wrapper">
      <FmlxCard
        id="license-violation-fmlx-card"
        disabled={false}
        draggable={false}
        selectable={false}
        selected={false}
        type=""
        variant={FmlxCard.Variant.OUTLINED}
      >
        <FmlxCard.Body>
          <LicenseHeader />
          <LicenseViolationBody
            headerText="License Violation"
            style={{ alignSelf: 'flex-start' }}
            statusCode={httpStatus}
          >
            {httpStatus === RMLicenseHttpStatusCode.SeatViolation && (
              <div id="license-violation-status-seat-violation">
                License could not be verified for this Seat. Please go to Setup to
                Register the Seat.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.ConcurrencyViolation && (
              <div id="license-violation-status-concurrency-violation">
                Rock Maker is already running at a miximum locations per your agreement.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.LicenseNotActive && (
              <div id="license-violation-status-license-not-active">
                Please activated the license to access Rock Maker software. Or you can
                choose trial license for temporary access Rock Maker software.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.AnonymousError && (
              <div id="license-violation-status-anonymous-error">
                Some error occured while checking the License.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.LicenseTerminated && (
              <div id="license-violation-status-license-terminated">
                Your Session has been terminated by another user. Any changes will be
                lost.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.DBError && (
              <div id="license-violation-status-db-error">
                A network-related or instance-specific error occurred while establishing a
                connection to SQL Server.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.LicenseUpdating && (
              <div id="license-violation-status-license-updating">
                License is being updated. Please try again after a few minutes.
              </div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.LicenseNotification && (
              <div id="license-violation-status-license-notification">{httpMessage}</div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.FeatureViolation && (
              <div id="license-violation-status-feature-violation">{`The feature you're trying to access is not available in the License.`}</div>
            )}

            {httpStatus === RMLicenseHttpStatusCode.EditionViolation && (
              <div id="license-violation-status-edition-violation">{`The feature you're trying to access is not available in the License.`}</div>
            )}
          </LicenseViolationBody>

          <div
            id="license-violation-button-wrapper"
            className="fields flex-x-center"
            style={{ alignSelf: 'center', marginTop: 10 }}
          >
            {httpStatus === RMLicenseHttpStatusCode.ExpiryViolation && (
              <div
                id="license-violation-button-expiry-violation-footer"
                className="licenseViolation__footer"
              >
                <FmlxButton
                  id="license-violation-button-footer-activate"
                  label="ACTIVATE"
                  onClick={activateButtonClicked}
                />
                <FmlxButton
                  id="license-violation-button-footer-request"
                  label="REQUEST"
                  onClick={requestButtonClicked}
                />
              </div>
            )}
            {httpStatus === RMLicenseHttpStatusCode.SeatViolation && (
              <div
                id="license-violation-button-seat-violation-footer-1"
                className="licenseViolation__footer"
              >
                <FmlxButton
                  id="license-violation-button-footer-setup"
                  label="SETUP"
                  onClick={adminToolsButtonClicked}
                />
              </div>
            )}
            {httpStatus !== RMLicenseHttpStatusCode.SeatViolation && (
              <div
                id="license-violation-button-seat-violation-footer-2"
                className="licenseViolation__footer"
              >
                <FmlxButton
                  id="license-violation-button-footer-close"
                  label="CLOSE"
                  onClick={closeWindow}
                />
              </div>
            )}
          </div>
        </FmlxCard.Body>
      </FmlxCard>
      {showLicenseRequestModal && (
        <LicenseRequest closeModal={() => setShowLicenseRequestModal(false)} />
      )}
      {showLicenseActivationModal && (
        <LicenseActivation closeModal={() => setShowLisenceActivationModal(false)} />
      )}
    </div>
  );
}
